// iter148.1 — "Son Baktığın Ürünler" — localStorage tabanlı gezinti hatırlatıcı
// Ürün detayında ziyaret edilen ürünler burada tutulur, anasayfa + ürün detayında görünür.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { fmtTRY } from "../../lib/format";
import { Clock, Package, X } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const STORAGE_KEY = "bs_recently_viewed";
const MAX_ITEMS = 12;

export function pushRecentlyViewed(product) {
  if (!product?.id) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    let list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) list = [];
    // Aynı ürünü çıkar, başa ekle
    list = list.filter((x) => x.id !== product.id);
    list.unshift({
      id: product.id,
      sku: product.sku || product.code || product.id,
      name: product.name,
      image: (Array.isArray(product.images) && product.images[0]) || product.image || "",
      brand_name: product.brand_name || "",
      price: product.price ?? product.unit_price ?? null,
      viewed_at: Date.now(),
    });
    list = list.slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch { /* ignore */ }
}

export function readRecentlyViewed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch { return []; }
}

export function clearRecentlyViewed() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

export default function RecentlyViewed({ excludeId = null, brandPrimary = "#8D1B3D", title = "Son Baktıklarınız" }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const list = readRecentlyViewed().filter((x) => x.id !== excludeId);
    setItems(list);
    // Fiyat/görsel eksik olan ürünleri backend'den zenginleştir (sessizce)
    (async () => {
      const need = list.filter((x) => !x.image || x.price == null);
      if (need.length === 0) return;
      const enriched = await Promise.all(need.map(async (x) => {
        try {
          const { data } = await axios.get(`${API}/api/shop/products/${x.sku || x.id}`);
          return { id: x.id, patch: { image: (data.images || [])[0] || data.image || "", price: data.price ?? data.unit_price ?? null, brand_name: data.brand_name || x.brand_name } };
        } catch { return null; }
      }));
      setItems((prev) => prev.map((it) => {
        const found = enriched.find((e) => e && e.id === it.id);
        return found ? { ...it, ...found.patch } : it;
      }));
    })();
  }, [excludeId]);

  const removeItem = (id) => {
    const list = items.filter((x) => x.id !== id);
    setItems(list);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* ignore */ }
  };

  if (items.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 py-6" data-testid="recently-viewed">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-zinc-500 mb-1">
            <Clock size={12}/> Kaldığınız Yerden Devam
          </div>
          <h2 className="font-display font-bold text-xl text-zinc-900">{title}</h2>
        </div>
        <button
          type="button"
          onClick={() => { clearRecentlyViewed(); setItems([]); }}
          className="text-xs text-zinc-500 hover:text-red-600"
          data-testid="rv-clear"
        >
          Listeyi Temizle
        </button>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3">
        {items.slice(0, 8).map((p) => (
          <div key={p.id} className="relative group" data-testid={`rv-item-${p.id}`}>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeItem(p.id); }}
              className="absolute top-1 right-1 z-10 w-6 h-6 rounded-full bg-white/90 border border-zinc-200 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:border-red-300 transition-all"
              aria-label="Kaldır"
              data-testid={`rv-remove-${p.id}`}
            >
              <X size={11} className="text-zinc-500 hover:text-red-600"/>
            </button>
            <Link
              to={`/urun/${p.sku || p.id}`}
              className="block border border-zinc-200 rounded-lg overflow-hidden hover:shadow-md hover:border-primary/40 transition-all bg-white"
            >
              <div className="aspect-square bg-zinc-50 overflow-hidden">
                {p.image
                  ? <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy"/>
                  : <div className="w-full h-full flex items-center justify-center"><Package size={22} className="text-zinc-300"/></div>}
              </div>
              <div className="p-2">
                <div className="text-[10px] text-zinc-500 uppercase truncate">{p.brand_name || "—"}</div>
                <div className="text-[11px] font-medium text-zinc-800 line-clamp-2 leading-tight">{p.name}</div>
                {p.price != null && p.price > 0 && (
                  <div className="text-xs font-bold mt-1 tabular-nums" style={{ color: brandPrimary }}>{fmtTRY(p.price)}</div>
                )}
              </div>
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}
